import React, { useEffect, useMemo, useState, memo, useRef, RefObject, useLayoutEffect, useCallback } from 'react';
// @ts-ignore
import { Droppable, Draggable, DroppableProvided } from '@nurba94/react-beautiful-dnd-custom-nurba';
import { useOnClickOutside } from 'usehooks-ts';
import { enqueueSnackbar } from 'notistack';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import cx from 'classnames';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';

import { toFollowPlayhead, updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { useLongPress } from '../../../helpers/useLongPress';
import { DROP_TYPE, TClipDrawer, UndoAction } from '..';
import { Portal } from '../../../helpers/bodyPortal';
import { TClip } from '../../Timeline/TimelineClip';
import { CLIP, NOTIFICATION } from '../../../enums';
import TrashButton from '../../modules/TrashButton';
import EditButton from '../../modules/EditButton';
import Modal from '../../modules/Modal';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { updateNotes, updateQualifiers } from '../../../store/videos/videoSlice';
import {
  updateCurrentSelectedComposerClips,
  updateComposerRowSelection,
  updateComposerPixelPosition,
  updateSelectedMultipleClips,
  updateComposerRows,
  updateIsDragging,
  triggerRefresh,
  updateCustomTime,
  setComposerMarkerPosition,
  setRowWidth,
  updateComposerPlayControls,
} from '../../../store/composers/composersSlice';
import styles from './ComposerRow.module.scss';
import deleteModalStyles from '../../Timeline/DeleteTimelineTopicModal/DeleteTimelineTopicModal.module.scss';
import DeleteRowModal from './DeleteRowModal';
import ComposerClip from '../ComposerClip';
import { useDimensions } from '../../../helpers';
import { findSearchValue } from '../../../helpers/getSearchValue';
import { RolesLowerCase } from '../../../enums/ROLES';
import { useClipFavourites } from '../../ClipFavourites/context';
import { useClipFilter } from '../../ClipFilter/context';

export type TComposerClip = {
  name: string;
  clips: TClip[];
  title: string;
  id: string;
  topicIndex?: number;
};

type ClipDropContainerProps = {
  clip: TClip;
  clipIndex: number;
  rowIndex: number;
  markMargin: number;
  clipIsSelected: boolean;
  topicId: string;
  topicName: string;
  clipElsRef: React.MutableRefObject<HTMLDivElement[]>;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDoubleClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  playheadEnabled: boolean;
  children: React.ReactNode;
};

const ClipDropContainer: React.FC<ClipDropContainerProps> = ({
  clip,
  clipIndex,
  rowIndex,
  markMargin,
  clipIsSelected,
  topicId,
  topicName,
  clipElsRef,
  onClick,
  onDoubleClick,
  playheadEnabled,
  children,
}) => {
  const dispatch = useAppDispatch();
  const { composerPlayControl } = useAppSelector((state) => state.composersStore);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const clipLength = useMemo(
    () => Math.max(0, (clip.time_end - clip.time_start) * markMargin),
    [clip.time_end, clip.time_start, markMargin],
  );

  const syncNotes = useCallback(() => {
    dispatch(updateQualifiers(clip.qualifiers));
    if (topicName || clip.notes) {
      const noteMessages = (clip.notes || []).map((n) => n.note);
      dispatch(updateNotes([noteMessages.map(() => topicName), noteMessages]));
    }
  }, [dispatch, clip.qualifiers, clip.notes, topicName]);

  const [, drop] = useDrop(
    () => ({
      accept: 'marker',
      hover: (_, monitor) => {
        const clientOffset = monitor.getClientOffset();
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds || !clientOffset || clipLength <= 0) return;
        const x = clientOffset.x - bounds.left;
        const ratio = Math.min(1, Math.max(0, x / clipLength));
        const time = clip.time_start + (clip.time_end - clip.time_start) * ratio;

        dispatch(updateCustomTime(time));
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playedSeconds: time,
          }),
        );
        dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
        syncNotes();

        if (!clipIsSelected) {
          dispatch(updateCurrentSelectedComposerClips([{ ...clip, clipIndex, rowIndex, topicId }]));
        }
      },
      drop: (_, monitor) => {
        const clientOffset = monitor.getClientOffset();
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds || !clientOffset || clipLength <= 0) return;
        const x = clientOffset.x - bounds.left;
        const ratio = Math.min(1, Math.max(0, x / clipLength));
        const timeOffset = clip.time_start + (clip.time_end - clip.time_start) * ratio;

        dispatch(updateCustomTime(timeOffset));
        dispatch(setComposerMarkerPosition({ clipIndex, rowIndex }));
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playedSeconds: timeOffset,
          }),
        );
        syncNotes();
        dispatch(updateCurrentSelectedComposerClips([{ ...clip, clipIndex, rowIndex, topicId }]));
      },
    }),
    [clip, clipIndex, rowIndex, clipLength, clipIsSelected, topicId, topicName, composerPlayControl.composerControls, dispatch, syncNotes],
  );

  const setDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (node) {
        drop(node);
        clipElsRef.current[clipIndex] = node;
      }
    },
    [clipElsRef, clipIndex, drop],
  );

  return (
    <div
      ref={setDropRef}
      className={styles.clipsContainer}
      data-composer-playhead-control={playheadEnabled ? 'true' : undefined}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      {children}
    </div>
  );
};

type TComposerRow = {
  topic: TComposerClip;
  rowIndex: number;
  selectClip: (
    clip: TClip,
    clipIndex: number,
    rowIndex: number,
    doubleClick?: boolean,
    clickEvt?: React.MouseEvent<HTMLDivElement>,
  ) => void;
  canDragRow: boolean;
  canDragClip: boolean;
  clipDrawer: TClipDrawer;
  handleEditClip: ({ id, time_end, time_start, color, topicId }: CLIP) => void;
  switchShowDeleteComposerClipModal: () => void;
  handleEditSubmit: (topicId: string, newName: string, rowIndex: number) => Promise<any>;
  setUndoStack: (a: any) => void;
  clipUndoMapRef: React.MutableRefObject<Record<string, UndoAction[]>>;
  renameUndoMapRef: React.MutableRefObject<Record<string, UndoAction[]>>;
  switchAdditionalDeleteComposerModal: () => void;
  isCollaborator: boolean;
  isDraggingClip: boolean;
  playheadVisible: boolean;
  switchSendClipModal: () => void;
  beforeMutatingComposer: () => Promise<boolean>;
  generatedClipIdSet: Set<string>;
  showGeneratedDownloadHints: boolean;
  collapsed: boolean;
  dimmedIds: Set<string>;
};

export const COMPOSER_CLIP_RIGHT_MARGIN = 12;

const ComposerRow: React.FC<TComposerRow> = ({
  topic,
  rowIndex,
  selectClip,
  canDragRow,
  clipDrawer,
  handleEditClip,
  switchShowDeleteComposerClipModal,
  canDragClip,
  handleEditSubmit,
  setUndoStack,
  clipUndoMapRef,
  renameUndoMapRef,
  switchAdditionalDeleteComposerModal,
  isCollaborator,
  isDraggingClip,
  playheadVisible,
  switchSendClipModal,
  beforeMutatingComposer,
  generatedClipIdSet,
  showGeneratedDownloadHints,
  collapsed,
  dimmedIds,
}) => {
  const { enterClip } = useClipFavourites();
  const { active: filterActive } = useClipFilter();
  const [isNameEdit, setIsNameEdit] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [activeRow, setActiveRow] = useState(false);
  const {
    composerPlayControl,
    composerRows,
    rowWidth,
    currentSelectedComposerClips,
    currentSelectedComposerRows,
    lastSelectedComposerRowId,
    customTime,
    refreshTrigger,
    markMargin,
    currentComposerData,
  } = useAppSelector((state) => state.composersStore);
  const { followPlayhead } = useAppSelector((state) => state.sharedStore);
  const { currentUser } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const [clips, setClips] = useState(topic.clips);
  const [showTopicDotsMenu, setShowTopicDotsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const longPressHandlers = useLongPress<HTMLDivElement>(handleNameEdit, 800);
  const isContentAdminOA = findSearchValue('allContent') === 'true' && currentUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN;
  const access = isContentAdminOA ? ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE : currentComposerData?.highest_user_access?.access?.title;
  const isOwner = currentUser?.id === currentComposerData?.created_by?.id;
  const canReshare = access === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE;
  const isSharedComposer = !!currentComposerData && currentComposerData?.created_by?.id !== currentUser?.id;
  const isReadOnlyShared = isSharedComposer && (access === ACCESS_TYPE_ENUM.VIEW || access === ACCESS_TYPE_ENUM.VIEW_AND_COMMENT);
  const canInteract = !isReadOnlyShared && (isOwner || canReshare || isCollaborator);
  const parentScroller = document.getElementById('composer_parent_scroller');
  const rowElementRef: RefObject<HTMLDivElement> = useRef(null);

  useEffect(() => {
    if (topic.clips) {
      setClips(topic.clips);
    }
  }, [topic]);

  useEffect(() => {
    if (composerPlayControl.currentRowIndex === rowIndex) {
      setActiveRow(true);
    } else {
      setActiveRow(false);
    }
  }, [composerPlayControl, composerRows, refreshTrigger]);

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: 'marker',
    collect: (monitor: { isDragging: () => boolean }) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  useEffect(() => {
    dispatch(updateIsDragging(isDragging));
    if (isDragging && followPlayhead) {
      dispatch(toFollowPlayhead(false));
    }
  }, [isDragging]);

  const { widthD, heightD } = useDimensions();
  const isMobile = widthD < 768;
  const mobileRowWidth = isMobile ? rowWidth : 0;

  const clipEls = useRef<HTMLDivElement[]>([]);
  const [rowLayoutRevision, setRowLayoutRevision] = useState(0);
  useLayoutEffect(() => {
    setRowLayoutRevision((revision) => revision + 1);
  }, [collapsed]);
  const markerPosition = useMemo(() => {
    if (!activeRow || rowIndex !== composerPlayControl.currentRowIndex) return { x: 0, y: 0 };
    if (isMobile && mobileRowWidth <= 0) return { x: 0, y: 0 };

    const { currentClipIndex } = composerPlayControl;
    const { playedSeconds } = composerPlayControl.composerControls;
    const currentClip = clips[currentClipIndex];

    if (!currentClip) return { x: 0, y: 0 };

    // raw elapsed time relative to clip start
    const rawElapsed = (playedSeconds > 0 ? playedSeconds : customTime) - currentClip.time_start;

    // duration of current clip
    const duration = Math.max(0, currentClip.time_end - currentClip.time_start);

    // clamp elapsed to [0, duration] so playhead never goes outside clip
    const clampedElapsed = Math.min(Math.max(rawElapsed, 0), duration);

    const scaled = clampedElapsed * markMargin;

    const el = clipEls.current[currentClipIndex];
    if (!el || !parentScroller || !rowElementRef.current) return { x: 0, y: 0 };

    const scrollerRect = parentScroller.getBoundingClientRect();
    const rowRect = rowElementRef.current.getBoundingClientRect();
    const clipRect = el.getBoundingClientRect();

    const x = clipRect.left - scrollerRect.left + (isMobile ? parentScroller.scrollLeft : 0) + scaled;
    const y = clipRect.top - rowRect.top;

    dispatch(updateComposerPixelPosition(x));
    return { x, y };
  }, [
    clips,
    markMargin,
    activeRow,
    composerPlayControl.currentRowIndex,
    composerPlayControl.currentClipIndex,
    composerPlayControl.composerControls.playedSeconds,
    customTime,
    widthD,
    heightD,
    mobileRowWidth,
    rowLayoutRevision,
  ]);

  const onEmptySpaceClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      const currentComposerClip = currentSelectedComposerClips.find(
        (clip) => clip.clipIndex === composerPlayControl.currentClipIndex && clip.rowIndex === composerPlayControl.currentRowIndex,
      );
      if (currentComposerClip) {
        dispatch(updateCurrentSelectedComposerClips([currentComposerClip]));
        const rows = JSON.parse(JSON.stringify(composerRows)) as TComposerClip[];
        for (let row = 0; row < rows.length; row++) {
          if (rows[row]?.clips && rows[row].clips.length > 0) {
            for (let clip = 0; clip < rows[row].clips.length; clip++) {
              if (rows[row].clips[clip]?.isSelected) {
                rows[row].clips[clip].isSelected = false;
              }
            }
          }
        }
        dispatch(updateComposerRows(rows));
        dispatch(updateSelectedMultipleClips(false));
        dispatch(triggerRefresh());
      }
    }
  };

  // Updated switchDotsMenu: remove manual clamp logic, use raw coords relative to container
  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // if (composerRows[rowIndex]?.clips?.length) {
    //   const clips = composerRows[rowIndex].clips.map((c, i) => ({ ...c, clipIndex: i, rowIndex }));
    //   dispatch(updateSelectedMultipleClips(false));
    //   dispatch(updateCurrentSelectedComposerClips(clips));
    //   dispatch(updateCustomTime(clips[0].time_start));
    //   dispatch(setComposerMarkerPosition({ rowIndex, clipIndex: 0 }));
    //   dispatch(triggerRefresh());
    // }

    if (!canInteract) return;

    setMenuPosition({ x: e.clientX + 20, y: e.clientY });
    setShowTopicDotsMenu(true);
  };

  useEffect(() => {
    if (!showTopicDotsMenu || !actionMenuRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = actionMenuRef.current;
    let { x, y } = menuPosition;

    if (x + w > window.innerWidth) x = window.innerWidth - w;
    if (y + h > window.innerHeight) y = window.innerHeight - h;
    if (x < 0) x = 0;
    if (y < 0) y = 0;

    const left = x + 20;
    setMenuPosition({ x: left, y });
  }, [showTopicDotsMenu]);

  const enablePlayheadFollower = () => {
    dispatch(toFollowPlayhead(true));
  };

  const onEditClose = () => {
    setIsNameEdit(false);
    setNewTopicName('');
  };

  const onNameSubmit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (topic.name.trim() !== newTopicName.trim() && isNameEdit) {
      if (!newTopicName.trim()) {
        setNewTopicName(topic.name);
        setTimeout(() => {
          enqueueSnackbar(NOTIFICATION.EMPTY, {
            variant: 'warning',
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          });
        }, 100);
        return;
      }

      handleEditSubmit(topic.id, newTopicName, rowIndex);
    }
    onEditClose();
  };

  const onNameEdit = (e: any) => {
    setNewTopicName(e.target.value);
  };

  useOnClickOutside(inputRef, (e) => {
    onNameSubmit(e);
  });

  useEffect(() => {
    if (activeRow && followPlayhead) {
      scrollRowIntoView(markerPosition.y);
      if (topic.clips.length === 0) {
        // dispatch(playNextComposerClip(true));
      }
    }
  }, [activeRow, followPlayhead, markerPosition.y]);

  const scrollRowIntoView = (markerOffset: number) => {
    const rowElement = rowElementRef.current;
    if (rowElement && parentScroller) {
      const parentRect = parentScroller.getBoundingClientRect();
      const rowRect = rowElement.getBoundingClientRect();
      const rowTop = parentScroller.scrollTop + rowRect.top - parentRect.top;
      parentScroller.scrollTo({
        top: Math.max(0, rowTop + markerOffset - (parentScroller.clientHeight - 61) / 2),
        left: parentScroller.scrollLeft,
      });
    }
  };

  const [showDeleteRowModal, setShowDeleteRowModal] = useState(false);
  const [showDeleteChoiceModal, setShowDeleteChoiceModal] = useState(false);
  const switchDeleteModal = () => {
    setShowDeleteRowModal(!showDeleteRowModal);
  };

  const selectedRowsToDelete = useMemo(() => {
    const selectedIds = new Set(currentSelectedComposerRows.map((row) => row.id));
    return composerRows.filter((row) => selectedIds.has(row.id));
  }, [composerRows, currentSelectedComposerRows]);

  const deleteRow = (e: any) => {
    e.preventDefault();
    const isCurrentRowSelected = selectedRowsToDelete.some((row) => row.id === topic.id);
    if (isCurrentRowSelected && selectedRowsToDelete.length > 1) {
      setShowDeleteChoiceModal(true);
      setShowTopicDotsMenu(false);
      return;
    }
    setShowDeleteRowModal(true);
    setShowTopicDotsMenu(false);
  };

  function handleNameEdit(e?: any) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!canInteract) return;
    setShowTopicDotsMenu(false);
    setNewTopicName(topic.name);
    setIsNameEdit(true);

    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  }

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowTopicDotsMenu(false);
  });

  const handleSecondaryClick = (e: React.MouseEvent<HTMLDivElement>, clipIndex: number) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(
      updateSecondaySelectedClips([
        { ...composerRows[rowIndex].clips[clipIndex], topicId: topic.id, clipIndex, rowIndex, topicName: topic.name },
      ]),
    );
    switchShowDeleteComposerClipModal();
  };

  const selectedIds = useMemo(() => new Set(currentSelectedComposerClips.map((c) => c.id)), [currentSelectedComposerClips]);
  const isEntireTopicSelected = useMemo(() => {
    return currentSelectedComposerRows.some((selectedRow) => selectedRow.id === topic.id);
  }, [currentSelectedComposerRows, topic.id]);
  useEffect(() => {
    if (isMobile || filterActive) return;
    if (currentSelectedComposerClips.length === 0 && composerRows.length) {
      const firstRowIndex = composerRows.findIndex((r) => r.clips?.length);
      if (firstRowIndex === -1) return; // no rows with clips

      const firstClip = composerRows[firstRowIndex].clips[0];
      const firstSelection = {
        ...firstClip,
        clipIndex: 0,
        rowIndex: firstRowIndex,
        topicId: composerRows[firstRowIndex].id,
        topicName: composerRows[firstRowIndex].name,
      };

      dispatch(updateCurrentSelectedComposerClips([firstSelection]));
      dispatch(updateCustomTime(firstClip.time_start));
      dispatch(setComposerMarkerPosition({ rowIndex: firstRowIndex, clipIndex: 0 }));
    }
  }, [currentSelectedComposerClips.length, composerRows, isMobile, filterActive]);

  // ComposerRow.tsx (inside useLayoutEffect)
  useLayoutEffect(() => {
    const el = document.getElementById('composerRows');
    if (!el) return;

    // set initial width
    // dispatch(setRowWidth(el.clientWidth));

    let frame: number | null = null;

    const ro = new ResizeObserver((entries) => {
      // schedule the React update for the next frame
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // ⚠️ read once, outside of React setState
        const w = entries[0].contentRect.width;
        dispatch(setRowWidth(w - 40)); // putting just w creates horizontal scrolling, so we remove 40 pixels from available width to get rid of the horizontal scrolling
        frame = null;
      });
    });

    ro.observe(el);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  const prevLinesRef = useRef<TClip[][]>([]);

  const lines = useMemo(() => {
    if (isDraggingClip) return prevLinesRef.current;

    const gap = COMPOSER_CLIP_RIGHT_MARGIN;
    const out: TClip[][] = [];
    let cur: TClip[] = [];
    let curW = 0;

    clips.forEach((c, i) => {
      const w = (c.time_end - c.time_start) * markMargin + gap;
      if (curW + w > rowWidth && cur.length) {
        out.push(cur);
        cur = [];
        curW = 0;
      }
      cur.push({ ...c, clipIndex: i });
      curW += w;
    });
    if (cur.length) out.push(cur);

    prevLinesRef.current = out;
    return out;
  }, [clips, markMargin, rowWidth, isDraggingClip]);

  useEffect(() => {
    if (!activeRow || !parentScroller || !followPlayhead) return;
    const scrollOffset = isMobile ? parentScroller.clientWidth / 2 : 400;
    parentScroller.scrollTo({ left: Math.max(0, markerPosition.x - scrollOffset) });
  }, [activeRow, followPlayhead, isMobile, markerPosition.x]);

  const onRowNumberClick = (e: React.MouseEvent<HTMLDivElement>, play?: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    const clickedRow = composerRows[rowIndex];
    if (!clickedRow?.clips?.length) return;

    const buildRowClips = (row: TComposerClip): TClip[] => {
      const selectedRowIndex = composerRows.findIndex((r) => r.id === row.id);
      if (selectedRowIndex === -1) return [];
      return row.clips.map((clip, clipIndex) => ({
        ...clip,
        clipIndex,
        rowIndex: selectedRowIndex,
        topicId: row.id,
        topicName: row.name,
      })).filter((clip) => !dimmedIds.has(String(clip.id)));
    };

    const sortClips = (clips: TClip[]) =>
      [...clips].sort((a, b) => {
        if ((a.rowIndex ?? 0) !== (b.rowIndex ?? 0)) return (a.rowIndex ?? 0) - (b.rowIndex ?? 0);
        return (a.clipIndex ?? 0) - (b.clipIndex ?? 0);
      });

    const mergeUniqueClips = (base: TClip[], extra: TClip[]) => {
      const byId = new Map<string, TClip>();
      [...base, ...extra].forEach((clip) => {
        byId.set(String(clip.id), clip);
      });
      return sortClips(Array.from(byId.values()));
    };

    const orderRowsAndBuildClips = (rows: TComposerClip[]): { orderedRows: TComposerClip[]; orderedClips: TClip[] } => {
      const selectedRowIds = new Set(rows.map((row) => row.id));
      const orderedRows = composerRows.filter((row) => selectedRowIds.has(row.id) && row.clips?.length);
      const orderedClips = orderedRows.flatMap((row) => buildRowClips(row));

      return { orderedRows, orderedClips };
    };

    const normalizedCurrentClips = sortClips(
      currentSelectedComposerClips.filter((clip) => !dimmedIds.has(String(clip.id))).map((clip) => {
        const row = composerRows.find((r) => r.id === clip.topicId) ?? composerRows[clip.rowIndex ?? -1];
        if (!row) return clip;
        const rowClip = row.clips.find((c) => c.id === clip.id);
        if (!rowClip) return clip;
        const normalizedRowIndex = composerRows.findIndex((r) => r.id === row.id);
        if (normalizedRowIndex === -1) return clip;
        const normalizedClipIndex = row.clips.findIndex((c) => c.id === clip.id);
        return {
          ...rowClip,
          rowIndex: normalizedRowIndex,
          clipIndex: normalizedClipIndex,
          topicId: row.id,
          topicName: row.name,
        };
      }),
    );

    const clickedRowAlreadySelected = currentSelectedComposerRows.some((row) => row.id === clickedRow.id);
    const rowModifierSelection = e.metaKey || e.ctrlKey || e.shiftKey;
    let nextRows: TComposerClip[] = [];
    let rowsInRange: TComposerClip[] = [];

    if (e.shiftKey && lastSelectedComposerRowId) {
      const anchorIndex = composerRows.findIndex((row) => row.id === lastSelectedComposerRowId);
      if (anchorIndex !== -1) {
        const [start, end] = anchorIndex < rowIndex ? [anchorIndex, rowIndex] : [rowIndex, anchorIndex];
        rowsInRange = composerRows.slice(start, end + 1).filter((row) => row.clips?.length);
        const mergedRowIds = new Set([...currentSelectedComposerRows.map((row) => row.id), ...rowsInRange.map((row) => row.id)]);
        nextRows = composerRows.filter((row) => mergedRowIds.has(row.id) && row.clips?.length);
      }
    }

    if (!nextRows.length) {
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        if ((e.metaKey || e.ctrlKey) && clickedRowAlreadySelected) {
          nextRows = currentSelectedComposerRows.filter((row) => row.id !== clickedRow.id && row.clips?.length);
        } else {
          const mergedRowIds = new Set([...currentSelectedComposerRows.map((row) => row.id), clickedRow.id]);
          nextRows = composerRows.filter((row) => mergedRowIds.has(row.id) && row.clips?.length);
        }
      } else {
        nextRows = [clickedRow];
      }
    }

    const { orderedRows, orderedClips } = orderRowsAndBuildClips(nextRows);
    const clickedRowClips = buildRowClips(clickedRow);
    let nextSelectedClips: TClip[] = orderedClips;
    if (rowModifierSelection) {
      if (rowsInRange.length) {
        const rangeClips = rowsInRange.flatMap((row) => buildRowClips(row));
        nextSelectedClips = mergeUniqueClips(normalizedCurrentClips, rangeClips);
      } else if ((e.metaKey || e.ctrlKey) && clickedRowAlreadySelected) {
        const clickedRowClipIds = new Set(clickedRow.clips.map((clip) => String(clip.id)));
        nextSelectedClips = sortClips(normalizedCurrentClips.filter((clip) => !clickedRowClipIds.has(String(clip.id))));
      } else {
        nextSelectedClips = mergeUniqueClips(normalizedCurrentClips, clickedRowClips);
      }
    }

    const nextLastSelectedRowId = lastSelectedComposerRowId === clickedRow.id ? '' : clickedRow.id;

    dispatch(
      updateComposerRowSelection({
        selectedRows: orderedRows,
        selectedClips: nextSelectedClips,
        lastSelectedRowId: nextLastSelectedRowId,
      }),
    );

    const rowStartIndex = clickedRow.clips.findIndex((clip) => !dimmedIds.has(String(clip.id)));
    const rowStartClip = clickedRow.clips[rowStartIndex];
    const clickedRowIsSelected = orderedRows.some((row) => row.id === clickedRow.id);
    if (!clickedRowIsSelected && rowIndex === composerPlayControl.currentRowIndex && nextSelectedClips.length) {
      const nextSelectedClip = nextSelectedClips.find((clip) => !dimmedIds.has(String(clip.id)));
      if (nextSelectedClip) {
        dispatch(setComposerMarkerPosition({ rowIndex: nextSelectedClip.rowIndex!, clipIndex: nextSelectedClip.clipIndex! }));
        dispatch(updateCustomTime(nextSelectedClip.time_start));
        dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: nextSelectedClip.time_start }));
      } else {
        dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }));
      }
    }
    const activeClipId = composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex]?.id;
    if (clickedRowIsSelected && (rowIndex !== composerPlayControl.currentRowIndex || dimmedIds.has(String(activeClipId)))) {
      if (rowStartClip) {
        dispatch(updateCustomTime(rowStartClip.time_start));
        dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: rowStartClip.time_start }));
        dispatch(setComposerMarkerPosition({ rowIndex, clipIndex: rowStartIndex }));
      } else {
        dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }));
      }
    }

    if (play && clickedRowIsSelected) {
      if (rowStartClip) {
        enterClip(rowStartClip.id);
        // we need this bump, because of the one useeffect that checks custom time
        const bump = Math.random() * (0.001 - 0.00001) + 0.00001;
        const start = rowStartClip.time_start + bump;
        dispatch(setComposerMarkerPosition({ rowIndex, clipIndex: rowStartIndex }));
        dispatch(updateCustomTime(start));
        dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: true, playedSeconds: start }));
      } else {
        dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }));
      }
    }

    dispatch(triggerRefresh());
  };

  const onPlayheadDoubleClick = () => {
    const currentClip = composerRows[composerPlayControl.currentRowIndex].clips[composerPlayControl.currentClipIndex];
    if (currentClip) {
      dispatch(updateCustomTime(currentClip.time_start));
      dispatch(
        setComposerMarkerPosition({ rowIndex: composerPlayControl.currentRowIndex, clipIndex: composerPlayControl.currentClipIndex }),
      );
      dispatch(
        updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: true, playedSeconds: currentClip.time_start }),
      );
    }
  };

  return (
    <>
      <Draggable draggableId={topic.id} index={rowIndex} disableInteractiveElementBlocking isDragDisabled={!canDragRow}>
        {(providedTopic: DroppableProvided) => (
          <div ref={rowElementRef} className={cx(styles.container, collapsed && styles.collapsed)} {...providedTopic.draggableProps}>
            <div className={styles.markerWrapper}>
              {/* real marker (existing) */}
              {activeRow &&
                playheadVisible &&
                rowIndex === composerPlayControl.currentRowIndex &&
                (!isMobile || mobileRowWidth > 0) && (
                  <>
                    <div
                      style={{
                        left: `${markerPosition.x}px`,
                        top: `${markerPosition.y}px`,
                        filter: followPlayhead ? 'brightness(150%)' : 'brightness(90%)',
                      }}
                      ref={drag}
                      data-composer-playhead-control="true"
                      className={cx(styles.composerMarker, composerPlayControl.isDragging && styles.activeMarker)}
                      onClick={enablePlayheadFollower}
                      onDoubleClick={(e) => onPlayheadDoubleClick()}
                    >
                      <div className={styles.pointer} />
                      <div className={styles.composerMarkerHead} />
                    </div>
                  </>
                )}
            </div>

            <div
              onClick={onRowNumberClick}
              onDoubleClick={(e) => onRowNumberClick(e, true)}
              ref={providedTopic.innerRef}
              {...providedTopic.dragHandleProps}
              className={cx(
                styles.order,
                isEntireTopicSelected && styles.orderSelected,
                isMobile && !canDragRow && styles.orderReadOnly,
              )}
              onContextMenu={switchDotsMenu}
            >
              {isMobile ? canDragRow && <DragIndicatorRoundedIcon className={styles.mobileRowDragHandle} /> : rowIndex + 1}
            </div>
            <div>
              {isNameEdit ? (
                <form
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onSubmit={onNameSubmit}
                >
                  <div className={styles.inputWrapper}>
                    <input className={styles.editText} onChange={onNameEdit} value={newTopicName} ref={inputRef} />
                  </div>
                </form>
              ) : (
                <div
                  onClick={onRowNumberClick}
                  onDoubleClick={(e) => onRowNumberClick(e, true)}
                  className={cx(
                    styles.rowName,
                    isMobile && !canDragRow && styles.rowNameReadOnly,
                    isMobile && isEntireTopicSelected && styles.mobileRowNameSelected,
                  )}
                  id={topic.id}
                  {...longPressHandlers}
                  onContextMenu={switchDotsMenu}
                >
                  {isMobile ? (
                    <>
                      <span className={styles.rowNameText}>{topic.name}</span>
                      <ChevronRightRoundedIcon className={styles.mobileRowChevron} />
                    </>
                  ) : (
                    topic.name
                  )}
                </div>
              )}

              <div className={styles.clipsWrapper}>
                {lines.length > 0 ? (
                  lines.map((line, lineIdx) => (
                    <Droppable
                      index={lineIdx}
                      key={lineIdx}
                      droppableId={`${topic.id}-${lineIdx}`}
                      type={DROP_TYPE.CLIP}
                      direction="horizontal"
                    >
                      {(prov: any) => (
                        <div ref={prov.innerRef} {...prov.droppableProps} className={styles.clipsLine}>
                          {line.map((composerClip, j) => (
                            <ClipDropContainer
                              key={composerClip.id}
                              clip={composerClip}
                              clipIndex={composerClip.clipIndex!}
                              rowIndex={rowIndex}
                              markMargin={markMargin}
                              clipIsSelected={selectedIds.has(composerClip.id)}
                              topicId={topic.id}
                              topicName={topic.name}
                              clipElsRef={clipEls}
                              onClick={(e) => selectClip(composerClip, composerClip.clipIndex!, rowIndex, false, e)}
                              onDoubleClick={(e) => selectClip(composerClip, composerClip.clipIndex!, rowIndex, true, e)}
                              playheadEnabled={clipDrawer?.id === composerClip.id}
                            >
                              <ComposerClip
                                dimmed={dimmedIds.has(String(composerClip.id))}
                                collapsed={collapsed}
                                clip={composerClip}
                                clipIndex={composerClip.clipIndex}
                                draggableIndex={j}
                                rowIndex={rowIndex}
                                handleEditClip={handleEditClip}
                                clipDrawer={clipDrawer}
                                markMargin={markMargin}
                                handleSecondaryClick={handleSecondaryClick}
                                topicId={topic.id}
                                rowTitle={topic.name}
                                canDrag={canDragClip}
                                selectClip={selectClip}
                                isCollaborator={!!isCollaborator}
                                clipIsSelected={selectedIds.has(composerClip.id)}
                                switchSendClipModal={switchSendClipModal}
                                hasGeneratedCopy={generatedClipIdSet.has(String(composerClip.id))}
                                showGeneratedDownloadHints={showGeneratedDownloadHints}
                              />
                            </ClipDropContainer>
                          ))}
                          {prov.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ))
                ) : (
                  <Droppable droppableId={`${topic.id}-0`} type={DROP_TYPE.CLIP} direction="horizontal">
                    {(prov: any, snap: any) => {
                      // show when dragging anywhere OR when directly over this row
                      const active = isDraggingClip || snap.isDraggingOver;
                      return (
                        <div
                          ref={prov.innerRef}
                          {...prov.droppableProps}
                          style={{
                            height: collapsed ? 12 : 35,
                            width: '100%',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 6,
                            transition: 'all 0.15s ease',
                            border: active ? '1px dashed #555' : '1px dashed transparent',
                            background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
                            opacity: active ? 0.95 : 0, // 👈 appears when dragging
                            pointerEvents: active ? 'auto' : 'none', // 👈 only catch drops when visible
                            marginLeft: `${COMPOSER_CLIP_RIGHT_MARGIN}px`,
                          }}
                        >
                          {active && <span style={{ fontSize: 12 }}>Drop clips here</span>}
                          {prov.placeholder}
                        </div>
                      );
                    }}
                  </Droppable>
                )}
              </div>

              <Portal>
                <div
                  ref={actionMenuRef}
                  className={styles.actionMenu}
                  style={{
                    position: 'absolute',
                    display: showTopicDotsMenu ? 'block' : 'none',
                    top: `${menuPosition.y}px`,
                    left: `${menuPosition.x}px`,
                  }}
                >
                  <div className={styles.menuOption} onClick={handleNameEdit}>
                    <EditButton /> Rename
                  </div>
                  <div className={styles.menuOption} onClick={deleteRow}>
                    <TrashButton /> Delete
                  </div>
                </div>
              </Portal>
            </div>
          </div>
        )}
      </Draggable>
      <DeleteRowModal
        topic={topic}
        switchModal={switchDeleteModal}
        open={showDeleteRowModal}
        setUndoStack={setUndoStack}
        clipUndoMapRef={clipUndoMapRef}
        renameUndoMapRef={renameUndoMapRef}
        switchAdditionalDeleteComposerModal={switchAdditionalDeleteComposerModal}
        beforeMutatingComposer={beforeMutatingComposer}
      />
      <Modal open={showDeleteChoiceModal} onClose={() => setShowDeleteChoiceModal(false)} loading={false}>
        <div className={deleteModalStyles.modalBody} onClick={(e) => e.stopPropagation()}>
          <div className={deleteModalStyles.modalTitle}>Delete current topic or all selected topics?</div>
          <div className={deleteModalStyles.actionBtns}>
            <OutlinedButton
              kind="default"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteChoiceModal(false);
                setShowDeleteRowModal(true);
              }}
            >
              Delete Topic {topic.name}
            </OutlinedButton>
            <ContainedButton
              kind="purple"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteChoiceModal(false);
                switchShowDeleteComposerClipModal();
              }}
            >
              Delete All Selected Topics
            </ContainedButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default memo(ComposerRow);
